/**
 * Live Start Page — living-wallpaper new-tab replacement
 *
 * Exposes broad globs — `pages/*`, `img/*`, `default-content/*`, `pages/common/mind/*`
 * — to <all_urls> with no `use_dynamic_url`, so every bundled file resolves from any
 * page at a fixed URL. The auth/site logo and the newtab popup stylesheet are stable
 * vendor paths.
 *
 * The site logo is the cleanest signal: an image loaded via `probeImage` with no CORS
 * grant, served only when the extension is installed.
 *
 * Manifest verified against version 40.5.8.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'ocggccaacacpienfcgmgcihoombokbbj';

export const liveStartPage: ExtensionDefinition = {
  id: ID,
  name: 'Live Start Page - Living Wallpapers',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 100_000,
  notes:
    'Broad WAR globs (pages/*, img/*) on <all_urls> with no dynamic URL. Detection via ' +
    'a stable logo/stylesheet path is conclusive and CORS-free.',
  signals: [
    {
      id: 'war-site-logo-image',
      kind: 'web-accessible-resource',
      description:
        'Load pages/common/auth/site_images/logo.png via <img> — exposed to every ' +
        'origin and readable without a CORS grant, surviving a strict page CSP.',
      weight: 1,
      run: () => probeImage(ID, 'pages/common/auth/site_images/logo.png'),
    },
    {
      id: 'war-mind-logo-image',
      kind: 'web-accessible-resource',
      description:
        'Load pages/common/mind/img/assets/logo.png via <img> — a second icon ' +
        'corroborating the match.',
      weight: 0.5,
      run: () => probeImage(ID, 'pages/common/mind/img/assets/logo.png'),
    },
    {
      id: 'war-newtab-popup-css',
      kind: 'web-accessible-resource',
      description:
        'Fetch pages/newtab/css/popup.css — a stylesheet path independent of the icons.',
      weight: 0.5,
      run: () => probeFetch(ID, 'pages/newtab/css/popup.css'),
    },
  ],
};
